/**
 * ActionBatches — Inteligência de Dados (Fase 4)
 * -----------------------------------------------------------------------------
 * Acompanhamento dos LOTES DE AÇÃO criados a partir do Explorador/Comparação:
 * progresso, sucessos, falhas, cancelamento e reprocessamento dos itens falhos.
 *
 * Todo o processamento roda em segundo plano (fila + worker). Esta tela apenas
 * lê o andamento via RPC paginada e atualiza a cada 15s.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 4 - Ações em lote).
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCcw,
  Rocket,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  ListPageFiltersBar,
  ListPageContent,
  ListPageFooter,
  KpiCard,
} from '@/components/layout/list-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/lib/toast';
import { useExternalDatasets } from '@/hooks/data/useExternalDatasets';
import {
  EXTERNAL_ACTION_LABELS,
  useExternalActionBatches,
  useExternalActionMutations,
  type ExternalActionType,
} from '@/hooks/data/useExternalActions';

const PAGE_SIZE = 20;

/** Aparência dos status de lote */
const STATUS_META: Record<
  string,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  pending: { label: 'Pendente', variant: 'secondary' },
  queued: { label: 'Na fila', variant: 'secondary' },
  running: { label: 'Processando', variant: 'outline' },
  completed: { label: 'Concluído', variant: 'default' },
  failed: { label: 'Falhou', variant: 'destructive' },
  canceled: { label: 'Cancelado', variant: 'secondary' },
  cancelled: { label: 'Cancelado', variant: 'secondary' },
};

/** Formata data/hora no padrão pt-BR */
const formatDateTime = (value: string | null) =>
  value ? format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '—';

/** Página "Ações em lote" */
export default function ActionBatches() {
  const [datasetFilter, setDatasetFilter] = useState<string>('all');
  const [page, setPage] = useState(0);

  const { datasets } = useExternalDatasets();
  const { batches, totalCount, isLoading, refetch } = useExternalActionBatches({
    datasetId: datasetFilter === 'all' ? null : datasetFilter,
    page,
    pageSize: PAGE_SIZE,
  });
  const { cancelBatch, retryBatch } = useExternalActionMutations();

  /** KPIs da página atual */
  const kpis = useMemo(() => {
    const running = batches.filter((b) => ['pending', 'queued', 'running'].includes(b.status)).length;
    const succeeded = batches.reduce((sum, b) => sum + Number(b.succeeded ?? 0), 0);
    const failed = batches.reduce((sum, b) => sum + Number(b.failed ?? 0), 0);
    return { running, succeeded, failed };
  }, [batches]);

  const totalPages = Math.max(1, Math.ceil(Number(totalCount || 0) / PAGE_SIZE));

  /** Cancela o lote (itens já processados permanecem) */
  const handleCancel = async (batchId: string) => {
    try {
      await cancelBatch.mutateAsync(batchId);
      toast.success('Lote cancelado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cancelar o lote');
    }
  };

  /** Recoloca os itens falhos na fila */
  const handleRetry = async (batchId: string) => {
    try {
      const requeued = await retryBatch.mutateAsync(batchId);
      toast.success(`${requeued} item(ns) reenviado(s) para processamento`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao reprocessar o lote');
    }
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Ações em lote"
        actions={
          <Button variant="outline" size="xs" onClick={() => refetch()}>
            <RotateCcw />
            Atualizar
          </Button>
        }
      />

      <ListPageKpis className="border-b-0 pb-1">
        <KpiCard color="orange" icon={Loader2} label="Em andamento" value={kpis.running} />
        <KpiCard
          color="green"
          icon={CheckCircle2}
          label="Sucessos nesta página"
          value={kpis.succeeded.toLocaleString('pt-BR')}
        />
        <KpiCard
          color="red"
          icon={AlertTriangle}
          label="Falhas nesta página"
          value={kpis.failed.toLocaleString('pt-BR')}
        />
      </ListPageKpis>

      <ListPageFiltersBar className="border-b-0 bg-transparent">
        <Select
          value={datasetFilter}
          onValueChange={(value) => {
            setDatasetFilter(value);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Dataset" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os datasets</SelectItem>
            {datasets.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent>
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : batches.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Rocket className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhuma ação em lote criada</p>
            <p className="text-xs text-muted-foreground">
              Filtre ou compare registros no Explorador e use o botão "Ações".
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {batches.map((batch) => {
              const meta = STATUS_META[batch.status] ?? STATUS_META.pending;
              const total = Number(batch.total_items ?? 0);
              const processed = Number(batch.processed ?? 0);
              const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
              const isActive = ['pending', 'queued', 'running'].includes(batch.status);

              return (
                <div key={batch.id} className="space-y-2 rounded-lg border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{batch.name}</span>
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                      <Badge variant="outline">
                        {EXTERNAL_ACTION_LABELS[batch.action_type as ExternalActionType] ??
                          batch.action_type}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{batch.dataset_name}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {batch.id.slice(0, 8).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(batch.created_at)}
                    </span>
                  </div>

                  {/* Progresso do processamento */}
                  <Progress value={percent} className="h-1.5" />

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>
                        Processados: {processed.toLocaleString('pt-BR')} /{' '}
                        {total.toLocaleString('pt-BR')}
                      </span>
                      <span>Sucesso: {Number(batch.succeeded ?? 0).toLocaleString('pt-BR')}</span>
                      <span>Ignorados: {Number(batch.skipped ?? 0).toLocaleString('pt-BR')}</span>
                      {Number(batch.failed ?? 0) > 0 && (
                        <span className="text-destructive">
                          Falhas: {Number(batch.failed).toLocaleString('pt-BR')}
                        </span>
                      )}
                      <span>Fim: {formatDateTime(batch.finished_at)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {isActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={cancelBatch.isPending}
                          onClick={() => handleCancel(batch.id)}
                        >
                          <XCircle className="h-4 w-4" />
                          Cancelar
                        </Button>
                      )}
                      {!isActive && Number(batch.failed ?? 0) > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={retryBatch.isPending}
                          onClick={() => handleRetry(batch.id)}
                        >
                          <RotateCcw className="h-4 w-4" />
                          Reprocessar falhas
                        </Button>
                      )}
                    </div>
                  </div>

                  {batch.error && <InlineNotice variant="error">{batch.error}</InlineNotice>}
                </div>
              );
            })}
          </div>
        )}
      </ListPageContent>

      <ListPageFooter>
        <span className="text-xs text-muted-foreground">
          {Number(totalCount || 0).toLocaleString('pt-BR')} lotes · página {page + 1} de {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </ListPageFooter>
    </ListPageLayout>
  );
}
